"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 141: Sachen 8259A (PCB TC-A003-72 / UNL-Sachen-8259A)
 * ------------------------------------------------------------------ *
 * Discrete-logic Sachen board: one 32 KB PRG bank at $8000 and four
 * 2 KB CHR windows, selected through two registers at $4100/$4101
 * (any address in $4100-$7FFF whose low wiring matches $C101 answers).
 *
 *   $4100: select which internal register the next $4101 write feeds
 *   $4101: data -> regs[0..3] = CHR low bits for PPU $0000/$0800/$1000/$1800
 *                 regs[4]    = CHR outer bank (upper bits, all four windows)
 *                 regs[5]    = 32 KB PRG bank
 *                 regs[7]    = bit 0: simple mode, bits 1-2: mirroring
 *
 * Variant A rewire: the CHR register feeds CHR A[17:12], while PPU
 * A[11:0] goes straight to the chip. Emulated by shifting the register
 * value up one bit (2 KB units) and ORing PPU A11 back in -- which is
 * a fixed 1, 0, 1 pattern for windows $0800/$1000/$1800.
 *
 * With simple mode (regs[7] bit 0) the four CHR windows all follow the
 * window-$0000 register, but the fixed low address lines still leak
 * through the rewiring.
 *
 * iNES 141 was once a Jaleco JF-14 alias (see mapper140.js); the number
 * now belongs to this board, like the wiki says.
 *
 * Used by Rockball, Po Po Team, Super Pang and Super Pang 2 (Sachen).
 * The Q-Boy cartridge (141 with CHR-RAM in the header) sets no CHR ROM:
 * the CHR selects are then not connected and CHR stays as console RAM.
 *
 * Reference: https://www.nesdev.org/wiki/Sachen_8259
 * Mesen: Core/Sachen8259.h (variant Sachen8259A)
 */

function register(Mappers) {
  Mappers[141] = function (nes) {
    this.nes = nes;
  };

  Mappers[141].prototype = new Mappers[0]();

  Mappers[141].prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);
    this.currentReg = 0;
    // regs[0..3] CHR low, regs[4] CHR outer, regs[5] PRG, regs[6] unused,
    // regs[7] mode/mirroring. Power-on state: all zero.
    this.regs = [0, 0, 0, 0, 0, 0, 0, 0];
  };

  Mappers[141].prototype.write = function (address, value) {
    if (address >= 0x4000 && address < 0x8000) {
      // Two 74'688s decode $4100/$4101 patterns anywhere in $4000-$7FFF;
      // the board has no PRG RAM, other writes in the range go nowhere.
      switch (address & 0xc101) {
        case 0x4100:
          this.currentReg = value & 0x07;
          break;
        case 0x4101:
          this.regs[this.currentReg] = value & 0x07;
          this.updateState();
          break;
      }
      return;
    }

    if (address >= 0x8000) {
      // PRG ROM: no register, no bus conflict, no WRAM.
      return;
    }

    Mappers[0].prototype.write.apply(this, arguments);
  };

  Mappers[141].prototype.updateState = function () {
    var rom = this.nes.rom;
    var simple = (this.regs[7] & 0x01) !== 0;

    // Mirroring: 0 = V, 1 = H, 2 = (0,1,1,1), 3 = 1-screen A.
    switch ((this.regs[7] >> 1) & 0x03) {
      case 1:
        this.setMirroring(rom.HORIZONTAL_MIRRORING);
        break;
      case 2:
        this.nes.ppu.setNametablePages([0, 1, 1, 1]);
        break;
      case 3:
        this.setMirroring(rom.SINGLESCREEN_MIRRORING);
        break;
      default:
        this.setMirroring(rom.VERTICAL_MIRRORING);
    }

    // One 32 KB bank at $8000 (the helper wraps to the ROM size).
    this.load32kRomBank(this.regs[5], 0x8000);

    // CHR: four 2 KB windows. Variant A: register value shifted up one
    // bit, PPU A11 leaks back in as the fixed OR bits 0,1,0,1.
    if (rom.vromCount > 0) {
      var chrHigh = this.regs[4] << 3;
      var pages = 2 * rom.vromCount; // CHR size in 2 KB units
      for (var w = 0; w < 4; w++) {
        var low = w === 0 ? this.regs[0] : this.regs[simple ? 0 : w];
        var page = (((chrHigh | low) << 1) | (w & 1 ? 1 : 0)) % pages;
        this.load2kVromBank(page, w * 0x0800);
      }
    }
  };

  Mappers[141].prototype.loadROM = function () {
    if (!this.nes.rom.valid || this.nes.rom.romCount < 1) {
      throw new Error("Sachen 8259: Invalid ROM! Unable to load.");
    }

    this.reset();
    this.loadTrainer();
    // Maps mirroring, PRG bank 0 and the power-on CHR windows.
    this.updateState();
    this.resetIrqHandler();
  };

  Mappers[141].prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    s.currentReg = this.currentReg;
    s.regs = this.regs.slice();
    return s;
  };

  Mappers[141].prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);
    this.currentReg = s.currentReg;
    this.regs = (s.regs || [0, 0, 0, 0, 0, 0, 0, 0]).slice();
    this.updateState();
  };
}

module.exports = { register: register };
